import React, { useState, useEffect, useRef } from 'react';
import './Chatbot.css'; 

// --- MODULE-LEVEL MEMORY CACHE ---
let inlineMessagesCache = [
    { text: "Welcome to Ai News News Assistant! Tell me what happened in your area and I will draft a professional news article for you directly.", sender: 'bot' }
];
let inlineLatestDraftCache = null;

let floatingMessagesCache = [
    { text: "Welcome to Ai News News Assistant! I can guide you on app features and help you draft your news post. What happened in your area today?", sender: 'bot' }
];
let floatingLatestDraftCache = null;

const generateNewsContentWithGemini = async (situation, isTelugu) => {
    const languageInstruction = isTelugu 
        ? "Draft the complete response professionally in TELUGU language and use Telugu script." 
        : "Draft the complete response professionally in English.";
    // Get today's dynamic date context to prevent AI from using blank date placeholders
    const today = new Date();
    const dateString = today.toLocaleDateString(isTelugu ? 'te-IN' : 'en-IN', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });

    const prompt = `
        You are an expert news assistant for a local news app in India (Ai News).
        Your task is to take a user's raw, unformatted situation report (often written in Romanized Telugu) and transform it into a professional, ready-to-post news article draft.
                Context:
        - Current Date: ${dateString} (Use this context to resolve references like "today", "yesterday", or specific dates in the news article, rather than using blank placeholders).
        Follow these instructions strictly:
        1. ${languageInstruction}
        2. Format the output with these EXACT headers: 
           శీర్షిక (Title): [Write the headline here]
           ముఖ్యాంశం (Summary): [Write the main body content here]
        3. Do not include any instructions, brackets, parenthesis, explanation, or meta-text. Just output the Title and Summary as requested.
        4. INTELLIGENT FACT FILLING: Do NOT use blank placeholders like [స్థానం], [తేదీ], or [సమయం] if the location, dates, or names are already present or can be clearly inferred from the user's situation input (e.g., if "Palasa" or "పలాస" is mentioned in the input, use it in the article). Only use a placeholder if a critical piece of information is completely missing and impossible to infer from the text.
        5. The tone must be highly professional, informative, and factual.
        6. User Situation: "${situation}"
    `;

    try {
        const response = await fetch(`https://us-central1-inewsbook-1402f.cloudfunctions.net/api/draft-news`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prompt, situation, language: isTelugu ? 'te' : 'en' })
        });

        if (!response.ok) {
            throw new Error('AI drafting service is currently unavailable.');
        }

        const data = await response.json();
        const draftedText = data.draftText; 
        
        const intro = isTelugu 
            ? "**Ai News అసిస్టెంట్:** మీ వివరాల ఆధారంగా, పోస్ట్ చేయడానికి సిద్ధంగా ఉన్న ముసాయిదా వార్తా కథనం ఇక్కడ ఉంది. దయచేసి వివరాలను సరిచూసుకోండి."
            : "**Assistant:** Based on your details, here is a ready-to-post news article draft. Please verify the details.";

        return `${intro}\n\n***\n\n${draftedText}`;

    } catch (error) {
        console.error("Gemini API Error:", error);
        return isTelugu 
            ? `క్షమించండి, వార్తా కంటెంట్‌ను రూపొందించడంలో లోపం జరిగింది. దయచేసి మీరే వ్రాయండి. (Error: ${error.message})`
            : `Sorry, an error occurred while drafting the news content. Please try again or write it manually. (Error: ${error.message})`;
    }
};

const qaPairs = [
    {
    keywords: ["features", "about", "గురించి", "ఫీచర్లు", "what can i do"],
    answer: {
      en: "Ai News has several great features:\n• Read Breaking & Category News.\n• **Post your own local news/wishes (with my drafting help!).**\n• Find and post Private & Government jobs.\n• **Report fake news** or suspicious posts.\n• Give image-based advertisements.",
      te: "Ai Newsలో అనేక గొప్ప ఫీచర్లు ఉన్నాయి:\n• బ్రేకింగ్ & వర్గాల వార్తలను చదవండి.\n• **మీ స్వంత స్థానిక వార్తలు/శుభాకాంక్షలను పోస్ట్ చేయండి (నా సహాయంతో!).**\n• ప్రైవేట్ & ప్రభుత్వ ఉద్యోగాలను కనుగొనండి మరియు పోస్ట్ చేయండి.\n• **నకిలీ వార్తలను రిపోర్ట్ చేయండి** లేదా అనుమానాస్పద పోస్ట్‌లను.\n• ఇమేజ్ ఆధారిత ప్రకటనలు ఇవ్వవచ్చు."
    }
  },
  {
    keywords: ["post", "job", "jobs", "ఉద్యోగం", "ఉద్యోగాలు", "payment", "fee", "ధర"],
    answer: {
      en: "You can post a job on the 'Jobs' page. **Your first job post is FREE for one month!** After that, if you wish to post another job or repost, you must pay a fee. Click on '+ Post a Job' to start.",
      te: "మీరు 'ఉద్యోగాలు' పేజీలో ఉద్యోగాన్ని పోస్ట్ చేయవచ్చు. **మీ మొదటి ఉద్యోగ పోస్ట్ ఒక నెల ఉచితం!** ఆ తర్వాత, మీరు మరో ఉద్యోగాన్ని పోస్ట్ చేయాలనుకుంటే లేదా తిరిగి పోస్ట్ చేయాలనుకుంటే, మీరు రుసుము చెల్లించాలి. ప్రారంభించడానికి '+ పోస్ట్ ఎ జాబ్' క్లిక్ చేయండి."
    }
  },
  {
    keywords: ["fake", "report", "తప్పుడు", "రిపోర్ట్", "చెక్", "తనిఖీ"],
    answer: {
      en: "To report fake news or a suspicious post, go to that specific post. Look for the 'Report' option (usually near the three-dot menu). Select 'Fake News' or the appropriate reason. Our support team will investigate immediately.",
      te: "నకిలీ వార్తను లేదా అనుమానాస్పద పోస్ట్‌ను రిపోర్ట్ చేయడానికి, ఆ పోస్ట్‌కు వెళ్లండి. అక్కడ మీరు 'రిపోర్ట్' ఆప్షన్ (సాధారణంగా మూడు చుక్కల మెనూ దగ్గర) కనుగొంటారు. 'నకిలీ వార్త' లేదా తగిన కారణాన్ని ఎంచుకోండి. మా సహాయక బృందం వెంటనే పరిశోధిస్తుంది."
    }
  },
  {
    keywords: ["post", "news", "వార్తలు", "పోస్ట్", "draft"],
    answer: {
      en: "To post news, please log in first. Once logged in, you will see a '+' icon in the top header. Click it and select 'Post News'. **If you need help drafting the content, just tell me the situation that happened!**",
      te: "వార్తలను పోస్ట్ చేయడానికి, దయచేసి ముందుగా లాగిన్ అవ్వండి. లాగిన్ అయిన తర్వాత, హెడర్‌లో '+' చిహ్నం కనిపిస్తుంది. దానిని క్లిక్ చేసి, 'పోస్ట్ న్యూస్' ఎంచుకోండి. **మీరు కంటెంట్‌ను రూపొందించడంలో సహాయం కావాలంటే, జరిగిన పరిస్థితిని నాకు చెప్పండి!**"
    }
  },
  {
    keywords: ["ad", "ads", "advertisement", "ప్రకటన", "image", "pay"],
    answer: {
      en: "You can post an image-based advertisement by paying the required amount. Please contact our marketing team directly for current rates and submission details at: support@newsbookindia.com or Contact No. : 9059900029.",
      te: "అవసరమైన మొత్తాన్ని చెల్లించడం ద్వారా మీరు ఇమేజ్ ఆధారిత ప్రకటనను పోస్ట్ చేయవచ్చు. ప్రకటనల విచారణలు లేదా సాంకేతిక మద్దతు కోసం, దయచేసి మమ్మల్ని support@newsbookindia.comలో సంప్రదించండి."
    }
  },
  {
    keywords: ["contact", "support", "help", "సహాయం", "సంప్రదించండి"],
    answer: {
      en: "For app usage guidance, ask me anything! For advertisement inquiries or technical support, please contact us at support@newsbookindia.com or call 9059900029.",
      te: "యాప్ వినియోగ మార్గదర్శకత్వం కోసం, నన్ను ఏదైనా అడగండి! ప్రకటనల విచారణలు లేదా సాంకేతిక మద్దతు కోసం, దయచేసి మమ్మల్ని support@newsbookindia.comలో సంప్రదించండి లేదా 9059900029కి కాల్ చేయండి."
    }
  },
  {
    keywords: ["hi", "hello", "hey", "నమస్కారం"],
    answer: {
      en: "Hello! I am the Ai News News Assistant. I can guide you on app features and help you draft your local news post! What happened in your area today?",
      te: "నమస్కారం! నేను Ai News న్యూస్ అసిస్టెంట్‌ని. నేను మీకు యాప్ ఫీచర్లు మరియు మీ స్థానిక వార్తా పోస్ట్‌ను రూపొందించడంలో సహాయం చేయగలను! ఈ రోజు మీ ప్రాంతంలో ఏం జరిగింది?"
    }
  },
  {
    keywords: ["how to use", "guide", "start", "tutorial", "ఎలా ఉపయోగించాలి", "గైడ్"],
    answer: {
      en: "Using Ai News is easy!\n1. Browse news and jobs on the homepage.\n2. To post news, click the '+' icon > 'Post News'.\n3. Remember: **Tell me the situation that happened, and I can draft the content for you!**",
      te: "Ai Newsని ఉపయోగించడం చాలా సులభం!\n1. హోమ్‌పేజీలో వార్తలు మరియు ఉద్యోగాలను బ్రావో చేయండి.\n2. వార్తలను పోస్ట్ చేయడానికి, '+' చిహ్నం > 'పోస్ట్ న్యూస్' క్లిక్ చేయండి.\n3. గుర్తుంచుకోండి: **జరిగిన పరిస్థితిని నాకు చెప్పండి, నేను మీ కోసం కంటెంట్‌ను రూపొందించగలను!**"
    }
  }
];

const getBotResponse = (userInput) => {
    const text = userInput.toLowerCase().trim();
    const hasTeluguScript = /[\u0C00-\u0C7F]/.test(text);
    const isLongInput = userInput.length > 20;
    const isTelugu = hasTeluguScript || isLongInput;

    if (isLongInput) {
        return { type: 'drafting', text: userInput, isTelugu };
    }

    for (const pair of qaPairs) {
        for (const keyword of pair.keywords) {
            if (text.includes(keyword.toLowerCase())) {
                return { type: 'guidance', text: isTelugu ? pair.answer.te : pair.answer.en };
            }
        }
    }

    const fallbackText = isTelugu 
        ? "क्षमించండి, దానిపై నాకు సమాచారం లేదు. నేను మీకు యాప్ ఫీచర్లు మరియు స్థానిక వార్తా కంటెంట్ రాయడంలో సహాయపడగలను. జరిగిన పరిస్థితిని నాకు చెప్పండి."
        : "I'm sorry, I don't have information on that. Describe the news situation that happened in your area and I will write the post draft for you.";
    
    return { type: 'guidance', text: fallbackText };
};

const parseAIDraft = (text) => {
    const sections = text.split('***');
    const mainDraft = sections.length > 1 ? sections[1] : text;

    const titleMarkers = ["శీర్షిక", "title"];
    const summaryMarkers = ["ముఖ్యాంశం", "summary", "కథనం ప్రారంభం", "article start"];

    const lines = mainDraft.split('\n')
        .map(line => line.replace(/\*/g, '').trim())
        .filter(line => line.length > 0);
    
    let title = "News Draft";
    let descriptionLines = [];
    let isCapturingDescription = false;

    const isTitleLine = (line) => titleMarkers.some(m => line.toLowerCase().includes(m));
    const isSummaryLine = (line) => summaryMarkers.some(m => line.toLowerCase().includes(m));

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (isTitleLine(line)) {
            const colonIndex = line.indexOf(':');
            if (colonIndex !== -1) {
                const potentialTitle = line.substring(colonIndex + 1).trim();
                if (potentialTitle.length > 0) {
                    title = potentialTitle;
                } else if (i + 1 < lines.length && !isSummaryLine(lines[i + 1]) && !isTitleLine(lines[i + 1])) {
                    title = lines[i + 1];
                    i++; // skip next line as it's consumed as the title
                }
            } else {
                // If there's no colon, try to consume the next line as the title
                if (i + 1 < lines.length && !isSummaryLine(lines[i + 1]) && !isTitleLine(lines[i + 1])) {
                    title = lines[i + 1];
                    i++; // skip next line as it's consumed as the title
                }
            }
            continue;
        }

        if (isSummaryLine(line)) {
            isCapturingDescription = true;
            const colonIndex = line.indexOf(':');
            if (colonIndex !== -1) {
                const potentialDesc = line.substring(colonIndex + 1).trim();
                if (potentialDesc.length > 0 && !potentialDesc.startsWith('(') && !potentialDesc.startsWith(')')) {
                    descriptionLines.push(potentialDesc);
                }
            }
            continue;
        }

        if (isCapturingDescription) {
            const cleanLine = line.trim();
            if (
                cleanLine === '(' || 
                cleanLine === ')' || 
                cleanLine === '):' || 
                cleanLine === '()' || 
                cleanLine.toLowerCase().includes('put the content') ||
                cleanLine.toLowerCase().includes('immediately after')
            ) {
                continue;
            }
            descriptionLines.push(line);
        }
    }

    // Fallback: If no summary block was captured, gather everything following the title
    if (descriptionLines.length === 0) {
        let titleFound = false;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (isTitleLine(line) || line === title) {
                titleFound = true;
                continue;
            }
            if (titleFound) {
                descriptionLines.push(line);
            }
        }
    }

    // Join all extracted text into a single cohesive paragraph and clean spaces
    let description = descriptionLines.join(' ').replace(/\s+/g, ' ').trim();
    
    // Clean Title from stray colons or brackets
    title = title.replace(/^[\s(:\-)]+|[\s):\-]+$/g, '').trim();

    return { 
        title: title.length > 100 ? title.substring(0, 97) + "..." : title, 
        description: description 
    };
};

const Chatbot = ({ onDraftApproved, isInline = false }) => {
    const [isOpen, setIsOpen] = useState(!isInline);
    const [userInput, setUserInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const chatboxBodyRef = useRef(null);

    // --- REVOLUTIONIZED STATE PERSISTENCE ---
    const [messages, setMessages] = useState(
        isInline ? inlineMessagesCache : floatingMessagesCache
    );
    const [latestDraft, setLatestDraft] = useState(
        isInline ? inlineLatestDraftCache : floatingLatestDraftCache
    );

    useEffect(() => {
        if (isInline) {
            inlineMessagesCache = messages;
        } else {
            floatingMessagesCache = messages;
        }
    }, [messages, isInline]);

    useEffect(() => {
        if (isInline) {
            inlineLatestDraftCache = latestDraft;
        } else {
            floatingLatestDraftCache = latestDraft;
        }
    }, [latestDraft, isInline]);

    useEffect(() => {
        if (chatboxBodyRef.current) {
            chatboxBodyRef.current.scrollTop = chatboxBodyRef.current.scrollHeight;
        }
    }, [messages, isTyping]);

    const handleCopyText = (text) => {
        navigator.clipboard.writeText(text);
        alert("Text copied!");
    };

    const handlePostClick = () => {
        if (!latestDraft) return;
        
        if (isInline && onDraftApproved) {
            onDraftApproved(latestDraft);
        } else {
            const event = new CustomEvent('openPostModalWithAI', { 
                detail: latestDraft 
            });
            window.dispatchEvent(event);
            setIsOpen(false); 
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!userInput.trim()) return;

        const currentInput = userInput;
        setUserInput('');

        const newUserMessage = { text: currentInput, sender: 'user' };
        setMessages(prev => [...prev, newUserMessage]);
        setIsTyping(true);

        const responseData = getBotResponse(currentInput);
        
        if (responseData.type === 'drafting') {
            const fullAiResponse = await generateNewsContentWithGemini(responseData.text, responseData.isTelugu);
            const parsed = parseAIDraft(fullAiResponse);
            setLatestDraft(parsed);
            
            setIsTyping(false);
            setMessages(prev => [...prev, 
                { text: fullAiResponse, sender: 'bot', isDraft: true }
            ]);
            
            setTimeout(() => {
                setMessages(prev => [...prev, { 
                    text: responseData.isTelugu 
                        ? "వార్త సిద్ధంగా ఉంది! దీనిని క్రింది ఫారమ్‌లోకి కాపీ చేయాలా?" 
                        : "The draft is ready! Automatically fill the details into the form below?", 
                    sender: 'bot', 
                    isSystemAction: true 
                }]);
            }, 1000);
        } else {
            setIsTyping(false);
            setMessages(prev => [...prev, { text: responseData.text, sender: 'bot' }]);
            setLatestDraft(null);
        }
    };

    // --- RENDER FOR INLINE CHAT HELPER ---
    if (isInline) {
        return (
            <div className={`chatbot-inline-container ${isOpen ? 'expanded' : 'collapsed'}`}>
                <div className="chatbot-inline-header" onClick={() => setIsOpen(!isOpen)}>
                    <h3>
                        <i className="fas fa-robot"></i> 
                        {isOpen ? " AI Assistant (వార్తా సహాయకుడు)" : " ✨ AI సహాయంతో క్షణాల్లో వార్త రాయండి (Draft News via AI)"}
                    </h3>
                    <button type="button" className="inline-toggle-btn">
                        <i className={`fas ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                    </button>
                </div>
                
                {isOpen && (
                    <div className="chatbot-inline-content">
                        <div className="chatbot-body chatbot-inline-body" ref={chatboxBodyRef}>
                            {messages.map((msg, index) => (
                                <div key={index} className={`chat-message ${msg.sender}`}>
                                    <div style={{ position: 'relative', width: '100%' }}>
                                        <p dangerouslySetInnerHTML={{ __html: msg.text.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br/>') }} />

                                        {msg.sender === 'bot' && !msg.isSystemAction && (
                                            <button className="copy-btn" onClick={() => handleCopyText(msg.text)}>
                                                <i className="fas fa-copy"></i>
                                            </button>
                                        )}

                                        {msg.isSystemAction && (
                                            <div className="chat-actions">
                                                <button type="button" className="chat-action-btn" onClick={handlePostClick}>
                                                    🚀 {msg.text.includes('ఫారమ్‌') ? 'అవును, పోస్ట్ చేయి' : 'Yes, Copy to Post Tab'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {isTyping && (
                                <div className="chat-message bot typing">
                                    <p>అసిస్టెంట్ ఆలోచిస్తున్నారు... (AI is drafting...)</p>
                                </div>
                            )}
                        </div>
                        <div className="chatbot-input-form">
                            <textarea
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="జరిగిన సంఘటనను ఇక్కడ టైప్ చేయండి (ఉదా: మా ఊర్లో నేడు వర్షం వల్ల రోడ్డు దెబ్బతింది...)" 
                                required
                                disabled={isTyping}
                                rows="2"
                            />
                            <button type="button" onClick={handleSendMessage} disabled={isTyping}>
                                <i className="fas fa-paper-plane"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // --- STANDARD FLOATING CHAT HELPER ---
    return (
        <div className="chatbot-container" style={{ width: '60px', height: '60px' }}>
            <button 
                id="tour-chatbot"
                className="chatbot-toggler" 
                onClick={() => setIsOpen(!isOpen)}
                style={{ position: 'fixed' }}
            >
                <i className={`fas ${isOpen ? 'fa-times' : 'fa-robot'}`}></i> 
            </button>

            {isOpen && (
                <div className="chatbot">
                    <div className="chatbot-header">
                        <h2>Ai News News Assistant</h2> 
                    </div>
                    <div className="chatbot-body" ref={chatboxBodyRef}>
                        {messages.map((msg, index) => (
                            <div key={index} className={`chat-message ${msg.sender}`}>
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <p dangerouslySetInnerHTML={{ __html: msg.text.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br/>') }} />

                                    {msg.sender === 'bot' && !msg.isSystemAction && (
                                        <button className="copy-btn" onClick={() => handleCopyText(msg.text)}>
                                            <i className="fas fa-copy"></i>
                                        </button>
                                    )}

                                    {msg.isSystemAction && (
                                        <div className="chat-actions">
                                            <button className="chat-action-btn" onClick={handlePostClick}>
                                                🚀 {msg.text.includes('వార్త') ? 'అవును, పోస్ట్ చేయి' : 'Yes, Copy to Post Tab'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="chat-message bot typing">
                                <p>అసిస్టెంట్ రాస్తున్నారు... (Drafting...)</p>
                            </div>
                        )}
                    </div>
                    <form className="chatbot-input-form" onSubmit={handleSendMessage}>
                        <textarea
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Type your question or the news situation..." 
                            required
                            disabled={isTyping}
                            rows="3"
                        />
                        <button type="submit" disabled={isTyping}><i className="fas fa-paper-plane"></i></button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default Chatbot;